import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, ChevronDown, Plus, Edit, Trash2, Download, Upload, Search, Eye, EyeOff, GripVertical, Lock, LockOpen, X } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import ConfirmationModal from '@/components/ConfirmationModal';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CSIMasterFormat = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [editingItem, setEditingItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [parentForNew, setParentForNew] = useState(null);
  
  // Initialize expandedNodes from URL
  const [expandedNodes, setExpandedNodes] = useState(() => {
    const expandedParam = searchParams.get('expanded');
    return expandedParam ? new Set(expandedParam.split(',')) : new Set();
  });
  
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    showCancel: true,
    onConfirm: () => {}
  });

  const [formData, setFormData] = useState({
    code: '',
    title: '',
    level: 4,
    parent_id: null
  });

  useEffect(() => {
    loadItems();
  }, []);

  useEffect(() => {
    filterItems();
  }, [searchQuery, items]);

  // Sync expandedNodes with URL changes (for browser back/forward)
  useEffect(() => {
    const expandedParam = searchParams.get('expanded');
    if (expandedParam) {
      setExpandedNodes(new Set(expandedParam.split(',')));
    } else {
      setExpandedNodes(new Set());
    }
  }, [searchParams]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/csi-masterformat`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setItems(response.data);
    } catch (error) {
      console.error('Error loading CSI MasterFormat:', error);
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to load CSI MasterFormat items.',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    if (!searchQuery.trim()) {
      setFilteredItems(items);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = items.filter(item => 
      item.title.toLowerCase().includes(query) ||
      (item.code && item.code.toLowerCase().includes(query))
    );
    setFilteredItems(filtered);
  };

  const buildTree = (parentId = null) => {
    return filteredItems
      .filter(item => item.parent_id === parentId)
      .sort((a, b) => a.sort_order - b.sort_order);
  };

  const toggleNode = (itemId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedNodes(newExpanded);
    
    // Update URL with expanded nodes
    const expandedArray = Array.from(newExpanded);
    if (expandedArray.length > 0) {
      searchParams.set('expanded', expandedArray.join(','));
    } else {
      searchParams.delete('expanded');
    }
    setSearchParams(searchParams, { replace: true });
  };

  const toggleSelectItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map(item => item.id)));
    }
  };

  const handleAdd = (parentItem = null) => {
    setParentForNew(parentItem);
    setFormData({
      code: '',
      title: '',
      level: parentItem ? parentItem.level + 1 : 1,
      parent_id: parentItem ? parentItem.id : null
    });
    setShowAddModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code || '',
      title: item.title,
      level: item.level,
      parent_id: item.parent_id
    });
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingItem) {
        // Update
        await axios.put(
          `${BACKEND_URL}/api/admin/csi-masterformat/${editingItem.id}`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` }}
        );
      } else {
        // Create
        await axios.post(
          `${BACKEND_URL}/api/admin/csi-masterformat`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` }}
        );
      }

      setEditingItem(null);
      setShowAddModal(false);
      loadItems();
      
      setConfirmModal({
        isOpen: true,
        title: 'Success',
        message: `Item ${editingItem ? 'updated' : 'created'} successfully!`,
        type: 'success',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    } catch (error) {
      console.error('Error saving item:', error);
      const errorMessage = error.response?.status === 423 
        ? error.response.data.detail 
        : 'Failed to save item. Please try again.';
      setConfirmModal({
        isOpen: true,
        title: error.response?.status === 423 ? 'Item Locked' : 'Error',
        message: errorMessage,
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    }
  };

  const handleDelete = async (itemId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this item? This action cannot be undone.',
      type: 'warning',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: () => {
        // OPTIMISTIC UPDATE: Remove item from state immediately
        const newItems = items.filter(item => item.id !== itemId);
        setItems(newItems);
        
        if (searchQuery) {
          const newFiltered = filteredItems.filter(item => item.id !== itemId);
          setFilteredItems(newFiltered);
        }
        
        // Show success message immediately
        setConfirmModal({
          isOpen: true,
          title: 'Success',
          message: 'Item deleted successfully!',
          type: 'success',
          confirmText: 'OK',
          showCancel: false,
          onConfirm: () => {}
        });

        // Backend request in background
        const token = localStorage.getItem('token');
        axios.delete(
          `${BACKEND_URL}/api/admin/csi-masterformat/${itemId}`,
          { headers: { 'Authorization': `Bearer ${token}` }}
        ).catch(error => {
          console.error('Error deleting item:', error);
          loadItems(); // Rollback on error
          const errorMessage = error.response?.status === 423 
            ? error.response.data.detail 
            : 'Failed to delete item. Reverting changes.';
          setConfirmModal({
            isOpen: true,
            title: error.response?.status === 423 ? 'Item Locked' : 'Error',
            message: errorMessage,
            type: 'danger',
            confirmText: 'OK',
            showCancel: false,
            onConfirm: () => {}
          });
        });
      }
    });
  };

  const handleToggleLock = async (item) => {
    const newLockState = !item.is_locked;
    const action = newLockState ? 'lock' : 'unlock';
    
    setConfirmModal({
      isOpen: true,
      title: newLockState ? 'Lock Item?' : 'Unlock Item?',
      message: newLockState 
        ? `Are you sure you want to lock "${item.title}"? Locked items cannot be edited or deleted.`
        : `Are you sure you want to unlock "${item.title}"? This will allow modifications.`,
      type: 'warning',
      confirmText: 'OK',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.put(
            `${BACKEND_URL}/api/admin/csi-masterformat/${item.id}`,
            { is_locked: newLockState },
            { headers: { 'Authorization': `Bearer ${token}` }}
          );
          
          // Update state optimistically
          const newItems = items.map(i => 
            i.id === item.id ? { ...i, is_locked: newLockState } : i
          );
          setItems(newItems);
          
          if (searchQuery) {
            const newFiltered = filteredItems.map(i => 
              i.id === item.id ? { ...i, is_locked: newLockState } : i
            );
            setFilteredItems(newFiltered);
          }
        } catch (error) {
          console.error('Error toggling lock:', error);
          setConfirmModal({
            isOpen: true,
            title: 'Error',
            message: 'Failed to toggle lock. Please try again.',
            type: 'danger',
            confirmText: 'OK',
            showCancel: false,
            onConfirm: () => {}
          });
        }
      }
    });
  };

  const handleToggleVisibility = async (item) => {
    // OPTIMISTIC UPDATE: Update state immediately
    const newHiddenValue = !item.is_hidden;
    const newItems = items.map(i => 
      i.id === item.id ? { ...i, is_hidden: newHiddenValue } : i
    );
    setItems(newItems);
    
    if (searchQuery) {
      const newFiltered = filteredItems.map(i => 
        i.id === item.id ? { ...i, is_hidden: newHiddenValue } : i
      );
      setFilteredItems(newFiltered);
    }

    // Backend request in background
    const token = localStorage.getItem('token');
    axios.put(
      `${BACKEND_URL}/api/admin/csi-masterformat/${item.id}`,
      { is_hidden: newHiddenValue },
      { headers: { 'Authorization': `Bearer ${token}` }}
    ).catch(error => {
      console.error('Error toggling visibility:', error);
      loadItems(); // Rollback on error
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to toggle visibility. Reverting changes.',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    });
  };

  const handleBulkDelete = () => {
    if (selectedItems.size === 0) {
      setConfirmModal({
        isOpen: true,
        title: 'No Selection',
        message: 'Please select items to delete.',
        type: 'warning',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
      return;
    }

    const selectedItemsArray = Array.from(selectedItems);
    const count = selectedItemsArray.length;

    setConfirmModal({
      isOpen: true,
      title: 'Confirm Bulk Delete',
      message: `Are you sure you want to delete ${count} item(s)? This action cannot be undone.`,
      type: 'warning',
      confirmText: 'Delete All',
      cancelText: 'Cancel',
      onConfirm: () => {
        // OPTIMISTIC UPDATE: Remove items from state immediately
        const newItems = items.filter(item => !selectedItems.has(item.id));
        setItems(newItems);
        
        if (searchQuery) {
          const newFiltered = filteredItems.filter(item => !selectedItems.has(item.id));
          setFilteredItems(newFiltered);
        }
        
        setSelectedItems(new Set());
        
        // Show success message immediately
        setConfirmModal({
          isOpen: true,
          title: 'Success',
          message: `${count} item(s) deleted successfully!`,
          type: 'success',
          confirmText: 'OK',
          showCancel: false,
          onConfirm: () => {}
        });

        // Backend request in background
        const token = localStorage.getItem('token');
        axios.post(
          `${BACKEND_URL}/api/admin/csi-masterformat/bulk-delete`,
          selectedItemsArray,
          { headers: { 'Authorization': `Bearer ${token}` }}
        ).catch(error => {
          console.error('Error bulk deleting:', error);
          loadItems(); // Rollback on error
          setConfirmModal({
            isOpen: true,
            title: 'Error',
            message: 'Failed to delete items. Reverting changes.',
            type: 'danger',
            confirmText: 'OK',
            showCancel: false,
            onConfirm: () => {}
          });
        });
      }
    });
  };

  const handleBulkShow = async () => {
    if (selectedItems.size === 0) {
      setConfirmModal({
        isOpen: true,
        title: 'No Selection',
        message: 'Please select items to show.',
        type: 'warning',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
      return;
    }

    const selectedItemsArray = Array.from(selectedItems);
    
    // OPTIMISTIC UPDATE: Update state immediately
    const newItems = items.map(item => 
      selectedItems.has(item.id) ? { ...item, is_hidden: false } : item
    );
    setItems(newItems);
    
    if (searchQuery) {
      const newFiltered = filteredItems.map(item => 
        selectedItems.has(item.id) ? { ...item, is_hidden: false } : item
      );
      setFilteredItems(newFiltered);
    }
    
    setSelectedItems(new Set());
    
    // Show success message immediately
    setConfirmModal({
      isOpen: true,
      title: 'Success',
      message: `${selectedItemsArray.length} item(s) shown successfully!`,
      type: 'success',
      confirmText: 'OK',
      showCancel: false,
      onConfirm: () => {}
    });

    // Backend requests in background
    const token = localStorage.getItem('token');
    Promise.all(
      selectedItemsArray.map(itemId =>
        axios.put(
          `${BACKEND_URL}/api/admin/csi-masterformat/${itemId}`,
          { is_hidden: false },
          { headers: { 'Authorization': `Bearer ${token}` }}
        )
      )
    ).catch(error => {
      console.error('Error bulk showing items:', error);
      loadItems(); // Rollback on error
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to show items. Reverting changes.',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    });
  };

  const handleBulkHide = async () => {
    if (selectedItems.size === 0) {
      setConfirmModal({
        isOpen: true,
        title: 'No Selection',
        message: 'Please select items to hide.',
        type: 'warning',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
      return;
    }

    const selectedItemsArray = Array.from(selectedItems);
    
    // OPTIMISTIC UPDATE: Update state immediately
    const newItems = items.map(item => 
      selectedItems.has(item.id) ? { ...item, is_hidden: true } : item
    );
    setItems(newItems);
    
    if (searchQuery) {
      const newFiltered = filteredItems.map(item => 
        selectedItems.has(item.id) ? { ...item, is_hidden: true } : item
      );
      setFilteredItems(newFiltered);
    }
    
    setSelectedItems(new Set());
    
    // Show success message immediately
    setConfirmModal({
      isOpen: true,
      title: 'Success',
      message: `${selectedItemsArray.length} item(s) hidden successfully!`,
      type: 'success',
      confirmText: 'OK',
      showCancel: false,
      onConfirm: () => {}
    });

    // Backend requests in background
    const token = localStorage.getItem('token');
    Promise.all(
      selectedItemsArray.map(itemId =>
        axios.put(
          `${BACKEND_URL}/api/admin/csi-masterformat/${itemId}`,
          { is_hidden: true },
          { headers: { 'Authorization': `Bearer ${token}` }}
        )
      )
    ).catch(error => {
      console.error('Error bulk hiding items:', error);
      loadItems(); // Rollback on error
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to hide items. Reverting changes.',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    });
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/csi-masterformat/export`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'csi_masterformat.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting:', error);
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to export data. Please try again.',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/admin/csi-masterformat/import`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      loadItems();
      setConfirmModal({
        isOpen: true,
        title: 'Success',
        message: 'Data imported successfully!',
        type: 'success',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    } catch (error) {
      console.error('Error importing:', error);
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to import data. Please try again.',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    }
  };

  const getLevelStyle = (level) => {
    switch(level) {
      case 1: return 'font-bold text-lg text-gray-900 bg-orange-50';
      case 2: return 'font-semibold text-base text-gray-800 bg-blue-50';
      case 3: return 'font-medium text-sm text-gray-700 bg-green-50';
      case 4: return 'text-sm text-gray-600';
      default: return 'text-sm text-gray-600';
    }
  };

  const handleDragEnd = async (event, parentId) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Get siblings (items with same parent)
    const siblings = buildTree(parentId);
    const oldIndex = siblings.findIndex(item => item.id === active.id);
    const newIndex = siblings.findIndex(item => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Reorder siblings array
    const reorderedSiblings = arrayMove(siblings, oldIndex, newIndex);

    // Calculate base sort_order (use minimum from siblings)
    const minSortOrder = Math.min(...siblings.map(s => s.sort_order));
    
    // Update sort_order for all affected siblings
    const updatedItems = reorderedSiblings.map((item, index) => ({
      id: item.id,
      sort_order: minSortOrder + index
    }));

    // OPTIMISTIC UPDATE: Update frontend state immediately
    const newItems = items.map(item => {
      const updated = updatedItems.find(u => u.id === item.id);
      return updated ? { ...item, sort_order: updated.sort_order } : item;
    });
    
    // Sort by new sort_order
    newItems.sort((a, b) => a.sort_order - b.sort_order);
    setItems(newItems);
    
    // Update filtered items too if search is active
    if (searchQuery) {
      const newFiltered = filteredItems.map(item => {
        const updated = updatedItems.find(u => u.id === item.id);
        return updated ? { ...item, sort_order: updated.sort_order } : item;
      });
      newFiltered.sort((a, b) => a.sort_order - b.sort_order);
      setFilteredItems(newFiltered);
    }

    // Send to backend in background (no await, no loading state)
    const token = localStorage.getItem('token');
    axios.put(
      `${BACKEND_URL}/api/admin/csi-masterformat/reorder`,
      { items: updatedItems },
      { headers: { 'Authorization': `Bearer ${token}` }}
    ).catch(error => {
      console.error('Error reordering items:', error);
      // On error, reload to get correct state from server
      loadItems();
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to save new order. Reverting changes.',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    });
  };

  const SortableItem = ({ item, depth, parentId }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: item.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const children = buildTree(item.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedNodes.has(item.id);
    const isSelected = selectedItems.has(item.id);
    const isHidden = item.is_hidden || false;
    const isLocked = item.is_locked || false;
    const isProtected = ['08 10 00', '08 40 01', '08 40 02', '08 50 00'].includes(item.code); // Protected items: Doors, Storefronts, Curtain Walls and Windows (used in all projects)

    return (
      <div ref={setNodeRef} style={{ ...style, marginLeft: `${depth * 24}px` }}>
        <div className={`flex items-center py-2 px-4 hover:bg-gray-100 border-b ${getLevelStyle(item.level)} ${isHidden ? 'opacity-50 bg-gray-100' : ''} ${isLocked ? 'bg-yellow-50' : ''}`}>
          {/* Lock/Unlock Toggle Icon */}
          <button
            onClick={() => handleToggleLock(item)}
            className="mr-2 cursor-pointer hover:bg-gray-100 rounded p-1"
            title={isLocked ? 'Locked - Click to unlock' : 'Unlocked - Click to lock'}
          >
            {isLocked ? (
              <Lock className="w-4 h-4 text-yellow-600" />
            ) : (
              <LockOpen className="w-4 h-4 text-gray-400" />
            )}
          </button>
          
          {/* Drag Handle */}
          <div {...attributes} {...listeners} className="cursor-move mr-2 text-gray-400 hover:text-gray-600">
            <GripVertical className="w-4 h-4" />
          </div>
          
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => toggleSelectItem(item.id)}
            className="mr-3"
            disabled={isLocked || isProtected}
            title={isProtected ? 'Protected item' : ''}
          />
          
          {hasChildren && (
            <button onClick={() => toggleNode(item.id)} className="mr-2">
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
          
          {!hasChildren && <div className="w-6 mr-2" />}
          
          <div className="flex-1 flex items-center gap-4">
            {item.code && <span className={`font-mono text-xs bg-white px-2 py-1 rounded ${isHidden ? 'text-gray-400' : ''}`}>{item.code}</span>}
            <span className={`flex-1 ${isHidden ? 'line-through text-gray-400' : ''}`}>{item.title}</span>
          </div>
          
          <div className="flex gap-2 items-center">
            {/* Show/Hide Toggle */}
            <button
              onClick={() => !isLocked && handleToggleVisibility(item)}
              disabled={isLocked}
              className={`p-1 rounded flex items-center gap-1 text-xs ${isLocked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'}`}
              title={isLocked ? 'Item is locked' : (isHidden ? "Show item" : "Hide item")}
            >
              {isHidden ? (
                <>
                  <EyeOff className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-500">Show</span>
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-600">Hide</span>
                </>
              )}
            </button>
            
            {item.level < 4 && (
              <button
                onClick={() => handleAdd(item)}
                className="p-1 hover:bg-gray-200 rounded"
                title="Add child"
              >
                <Plus className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => !isLocked && handleEdit(item)}
              disabled={isLocked}
              className={`p-1 rounded ${isLocked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-200'}`}
              title={isLocked ? 'Item is locked' : 'Edit'}
            >
              <Edit className="w-4 h-4" />
            </button>
            {!isProtected && (
              <button
                onClick={() => !isLocked && handleDelete(item.id)}
                disabled={isLocked}
                className={`p-1 rounded ${isLocked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-200'}`}
                title={isLocked ? 'Item is locked' : 'Delete'}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            {isProtected && (
              <div className="p-1 opacity-50 cursor-not-allowed" title="This item is protected and cannot be deleted">
                <Trash2 className="w-4 h-4 text-gray-300" />
              </div>
            )}
          </div>
        </div>
        
        {hasChildren && isExpanded && (
          <SortableList items={children} parentId={item.id} depth={depth + 1} />
        )}
      </div>
    );
  };

  const SortableList = ({ items, parentId, depth }) => {
    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => handleDragEnd(event, parentId)}
      >
        <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
          {items.map(item => (
            <SortableItem key={item.id} item={item} depth={depth} parentId={parentId} />
          ))}
        </SortableContext>
      </DndContext>
    );
  };

  const renderTreeNode = (item, depth = 0) => {
    return <SortableItem item={item} depth={depth} parentId={item.parent_id} />;
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h2 className="text-3xl font-bold mb-2" style={{ background: '#FF6600', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Specifications
            </h2>
            <p className="text-gray-600">Manage construction specification divisions and items</p>
          </div>

          {/* Toolbar */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => handleAdd()}
              style={{ background: '#FF6600' }}
              className="px-4 py-2 text-white rounded hover:opacity-90 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Root Item
            </button>
            
            <button
              onClick={handleBulkShow}
              disabled={selectedItems.size === 0}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Show Selected ({selectedItems.size})
            </button>

            <button
              onClick={handleBulkHide}
              disabled={selectedItems.size === 0}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <EyeOff className="w-4 h-4" />
              Hide Selected ({selectedItems.size})
            </button>

            <button
              onClick={handleBulkDelete}
              disabled={selectedItems.size === 0}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete Selected ({selectedItems.size})
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            
            <label className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-2 cursor-pointer">
              <Upload className="w-4 h-4" />
              Import CSV
              <input
                type="file"
                accept=".csv"
                onChange={handleImport}
                className="hidden"
              />
            </label>
          </div>
        </div>

          {/* Search */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by code or title..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              {/* Clear Filter Button */}
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Tree View */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden flex-1 flex flex-col">
          <div className="flex items-center py-3 px-4 bg-gray-50 border-b font-semibold">
            <Checkbox
              checked={selectedItems.size === filteredItems.length && filteredItems.length > 0}
              onCheckedChange={toggleSelectAll}
              className="mr-3"
            />
            <span>Select All</span>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredItems.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No items found</div>
          ) : (
            <div className="overflow-y-auto flex-1">
              <SortableList items={buildTree(null)} parentId={null} depth={0} />
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        {(showAddModal || editingItem) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">
                {editingItem ? 'Edit Item' : 'Add New Item'}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Code</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    placeholder="e.g., 00 01 00"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g., Maintenance of Specification"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Level</label>
                  <select
                    value={formData.level}
                    onChange={(e) => setFormData({...formData, level: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value={1}>Group</option>
                    <option value={2}>Subgroup</option>
                    <option value={3}>Division</option>
                    <option value={4}>Item</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleSave}
                  style={{ background: '#FF6600' }}
                  className="flex-1 px-4 py-2 text-white rounded hover:opacity-90"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditingItem(null);
                    setShowAddModal(false);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <ConfirmationModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({...confirmModal, isOpen: false})}
          onConfirm={() => {
            confirmModal.onConfirm();
            setConfirmModal({...confirmModal, isOpen: false});
          }}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          confirmText={confirmModal.confirmText}
          cancelText={confirmModal.cancelText}
          showCancel={confirmModal.showCancel}
        />
        </div>
      </div>
    </AdminLayout>
  );
};

export default CSIMasterFormat;
